/**
 * Uploading a map image.
 *
 * **Raw bytes, not multipart.** The endpoint is `express.raw` over an allowlist
 * of image content types, so the file's own type *is* the whole negotiation —
 * which is also what makes the server's check the same check the browser sent.
 *
 * Its own module because two surfaces need it: the setup flow, where a map is
 * now required before a table opens, and the Owner panel, where one is
 * replaced later. Two copies of a wire contract is how the two drift.
 */

import { ApiError } from '../api.js';

/** What the server accepts, mirrored here only to fill the file picker. */
export const MAP_IMAGE_TYPES = 'image/png,image/jpeg,image/webp,image/gif';

/**
 * The same two limits, in words, for the surfaces that have to *say* them.
 *
 * Here rather than in each page, because there are three of them now — the
 * guide, the setup wizard and the map panel — and a limit stated differently in
 * two places is one a player is told wrongly in at least one of them. The
 * numbers themselves belong to `routes/assets.ts`; these are its prose.
 */
export const MAP_IMAGE_TYPES_LABEL = 'PNG, JPEG, WebP or GIF';
export const MAX_MAP_UPLOAD_LABEL = '25 MB';

/**
 * Uploads the file and returns its asset key.
 *
 * ⚠ **The key, not `void`** — since 2026-09-01 the setup wizard needs it to
 * *activate* a world map (`PUT .../world-map`), which is a second call this
 * function does not make: uploading only ever wrote an `assets` row, never
 * `campaigns.world_map_key`. Returning nothing here silently threw away the
 * one thing a caller would need to finish the job.
 */
export async function uploadMapImage(
  campaignId: string,
  kind: 'world_map' | 'encounter_map',
  file: File,
): Promise<string> {
  const res = await fetch(`/api/campaigns/${campaignId}/assets?kind=${kind}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': file.type },
    body: file,
  });
  if (res.ok) {
    const body = (await res.json()) as { asset: { key: string } };
    return body.asset.key;
  }

  // The server's own words where it has any: it knows whether this was the
  // type or the size, and a paraphrase here would be a second, worse copy of
  // two limits that live in `routes/assets.ts`.
  const body: unknown = await res.json().catch(() => undefined);
  const message =
    typeof body === 'object' && body !== null && 'error' in body
      ? String((body as { error: unknown }).error)
      : `upload failed (${res.status})`;
  throw new ApiError(res.status, message);
}
